// Client-side mirror of ARI's real reservation engine (backend/reservations.py) for the
// landing-page sample booking. Same defaults, table logic and deposit rules; sample data only.
export const CONFIG = {
  slotInterval: 15,
  turn: 90,
  largeTurn: 120,
  largeThreshold: 5,
  maxParty: 20,
  maxCombine: 3,
  holdSeconds: 600,
  periods: [
    { id: "lunch", name: "Lunch", start: "12:00", end: "14:30" },
    { id: "dinner", name: "Dinner", start: "18:00", end: "22:00" },
  ],
  objectives: ["BALANCE_SERVICE_FLOW", "PROTECT_LARGE_TABLES"],
  deposit: { partyThreshold: 6, peakPeriods: ["dinner"], peakDays: [4, 5], perGuest: 1000, noShowPerGuest: 1500 },
};

export const TABLES = [
  { id: "T1", min: 1, capacity: 2, x: 12, y: 24, shape: "round" },
  { id: "T2", min: 1, capacity: 2, x: 32, y: 24, shape: "round" },
  { id: "T3", min: 2, capacity: 4, x: 56, y: 24, shape: "square" },
  { id: "T4", min: 2, capacity: 4, x: 80, y: 24, shape: "square" },
  { id: "T5", min: 3, capacity: 6, x: 14, y: 70, shape: "rect" },
  { id: "T6", min: 4, capacity: 8, x: 36, y: 70, shape: "rect" },
  { id: "T7", min: 6, capacity: 10, x: 59, y: 70, shape: "long" },
  { id: "T8", min: 8, capacity: 12, x: 82, y: 70, shape: "long" },
];

export const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
export const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
export const turnFor = (party) => (party >= CONFIG.largeThreshold ? CONFIG.largeTurn : CONFIG.turn);

// Deterministic sample bookings so the floor looks like a real service (busier Fri/Sat).
export const sampleBusy = (dayIndex, weekday) => {
  let seed = (dayIndex + 3) * 9301 + weekday * 49297;
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const density = weekday === 4 || weekday === 5 ? 0.6 : 0.38;
  const busy = {};
  TABLES.forEach((t) => {
    busy[t.id] = [];
    CONFIG.periods.forEach((p) => {
      let cur = toMin(p.start);
      while (cur <= toMin(p.end)) {
        if (rnd() < (cur === toMin(p.start) ? density * 0.45 : density)) {
          const len = t.capacity >= 6 ? CONFIG.largeTurn : CONFIG.turn;
          busy[t.id].push([cur, cur + len]);
          cur += len + 15 * Math.floor(rnd() * 4);
        } else cur += 30;
      }
    });
  });
  return busy;
};

const overlaps = (ivs, s, e) => ivs.some(([a, b]) => s < b && e > a);

export const pickTable = (party, busy, s, e) => {
  let best = null, bestScore = -1e9;
  TABLES.forEach((t) => {
    if (t.capacity < party || party < t.min) return;
    const ivs = busy[t.id] || [];
    if (overlaps(ivs, s, e)) return;
    let score = -(t.capacity - party) * 10;
    if (CONFIG.objectives.includes("PROTECT_LARGE_TABLES")) score -= Math.max(0, t.capacity - party) * 6;
    if (CONFIG.objectives.includes("BALANCE_SERVICE_FLOW")) score -= ivs.length * 8;
    if (score > bestScore) { bestScore = score; best = t; }
  });
  return best;
};

export const pickAllocation = (party, busy, s, e) => {
  const single = pickTable(party, busy, s, e);
  if (single) return [single];
  const free = TABLES.filter((t) => !overlaps(busy[t.id] || [], s, e)).sort((a, b) => b.capacity - a.capacity);
  const chosen = []; let sum = 0;
  for (const t of free) {
    if (chosen.length >= CONFIG.maxCombine) break;
    chosen.push(t); sum += t.capacity;
    if (sum >= party) return chosen;
  }
  return null;
};

export const availability = (party, periodId, busy) => {
  const p = CONFIG.periods.find((x) => x.id === periodId);
  const turn = turnFor(party);
  const times = [];
  let total = 0;
  for (let m = toMin(p.start); m <= toMin(p.end); m += CONFIG.slotInterval) {
    total += 1;
    if (pickAllocation(party, busy, m, m + turn)) times.push(toHHMM(m));
  }
  return { times, total, turn };
};

export const depositRequired = (mode, party, periodId, weekday) => {
  if (mode === "off") return { required: false };
  const d = CONFIG.deposit;
  const isParty = party >= d.partyThreshold;
  const peak = d.peakPeriods.includes(periodId) || d.peakDays.includes(weekday);
  if (!(isParty || peak)) return { required: false, isParty, peak };
  const per = mode === "deposit" ? d.perGuest : d.noShowPerGuest;
  return { required: true, mode, per, amount: per * party, isParty, peak };
};

export const money = (minor) => `£${(minor / 100).toFixed(2)}`;

export const resCode = () => "ARI-" + Array.from({ length: 6 }, () => "ABCDEFGHJKMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 31)]).join("");
